/**
 * useStackRealtimePolling — REST data layer for the StackRealtime provider.
 * Owns all `axios`-based fetch callbacks, the one-shot initial load,
 * and the org-switch refetch effect.
 *
 * SSE (see useStackRealtimeSse) is the realtime path — EventSource
 * auto-reconnects natively, so there is no REST polling fallback here.
 * On SSE disconnect, a one-shot REST refresh primes the data; SSE resumes
 * the stream when the connection is restored.
 */

import { useCallback, useEffect, useState } from "react";

import axios from "axios";

import { ApplicationEndpoints } from "@generated/application";
import { OrgSessionEvents } from "@platform/constants/Events";
import { apiBaseUrls } from "@platform/constants/shared-constants";
import { eventBus } from "@platform/events/EventBus";

import type { StackMetrics } from "../pages/StackDetailsPage/types";

import { normalizeEvent } from "./normalizeEvent";
import type { DeploymentEvent } from "./useDeploymentEvents";
import type { PipelineStep } from "./useDeploymentStream";
import type { GitOpsStatus } from "./useGitOpsStatus";
import type { K8sEventSnapshot } from "./useK8sEvents";
import type { PodMonitorSnapshot } from "./usePodMonitor";

export type LogBatchesMap = Map<string, Map<string, Map<string, string[]>>>;
export type LogBatchesSetter = React.Dispatch<
  React.SetStateAction<LogBatchesMap>
>;

export interface StackRealtimePollingState {
  events: DeploymentEvent[];
  pipelineState: PipelineStep[];
  logBatches: Map<string, Map<string, Map<string, string[]>>>;
  deploymentLoading: boolean;
  deploymentError: string | null;
  orgMismatch: boolean;
  pipelineRunRefreshSignal: number;
  status: string | null;
  deploymentUrl: string | null;
  podSnapshot: PodMonitorSnapshot | null;
  podLoading: boolean;
  podError: string | null;
  k8sEvents: K8sEventSnapshot[];
  k8sEventsLoading: boolean;
  k8sEventsError: string | null;
  gitopsStatus: GitOpsStatus | null;
  gitopsLoading: boolean;
  gitopsError: string | null;
  metrics: StackMetrics | null;
  metricsLoading: boolean;

  setEvents: React.Dispatch<React.SetStateAction<DeploymentEvent[]>>;
  setPipelineState: React.Dispatch<React.SetStateAction<PipelineStep[]>>;
  setLogBatches: LogBatchesSetter;
  setDeploymentError: React.Dispatch<React.SetStateAction<string | null>>;
  setDeploymentLoading: React.Dispatch<React.SetStateAction<boolean>>;
  setStatus: React.Dispatch<React.SetStateAction<string | null>>;
  setDeploymentUrl: React.Dispatch<React.SetStateAction<string | null>>;
  setPodSnapshot: React.Dispatch<
    React.SetStateAction<PodMonitorSnapshot | null>
  >;
  setPodLoading: React.Dispatch<React.SetStateAction<boolean>>;
  setPodError: React.Dispatch<React.SetStateAction<string | null>>;
  setK8sEvents: React.Dispatch<React.SetStateAction<K8sEventSnapshot[]>>;
  setK8sEventsLoading: React.Dispatch<React.SetStateAction<boolean>>;
  setK8sEventsError: React.Dispatch<React.SetStateAction<string | null>>;
  setGitopsStatus: React.Dispatch<React.SetStateAction<GitOpsStatus | null>>;
  setGitopsLoading: React.Dispatch<React.SetStateAction<boolean>>;
  setGitopsError: React.Dispatch<React.SetStateAction<string | null>>;
  setMetrics: React.Dispatch<React.SetStateAction<StackMetrics | null>>;
  setPipelineRunRefreshSignal: React.Dispatch<React.SetStateAction<number>>;

  fetchDeploymentEvents: () => Promise<void>;
  fetchPipelineState: () => Promise<PipelineStep[]>;
  fetchPods: () => Promise<void>;
  fetchK8sEvents: () => Promise<void>;
  fetchGitOps: () => Promise<void>;
  fetchMetrics: () => Promise<void>;
}

export function useStackRealtimePolling(
  alias: string,
): StackRealtimePollingState {
  // ── Deployment pipeline state ────────────────────────────────────────────
  const [events, setEvents] = useState<DeploymentEvent[]>([]);
  const [pipelineState, setPipelineState] = useState<PipelineStep[]>([]);
  const [logBatches, setLogBatches] = useState<
    Map<string, Map<string, Map<string, string[]>>>
  >(new Map());
  const [deploymentLoading, setDeploymentLoading] = useState(true);
  const [deploymentError, setDeploymentError] = useState<string | null>(null);
  const [orgMismatch, _setOrgMismatch] = useState(false);
  const [pipelineRunRefreshSignal, setPipelineRunRefreshSignal] = useState(0);

  // ── System status ────────────────────────────────────────────────────────
  const [status, setStatus] = useState<string | null>(null);
  const [deploymentUrl, setDeploymentUrl] = useState<string | null>(null);

  // ── Pod monitoring state ──────────────────────────────────────────────────
  const [podSnapshot, setPodSnapshot] = useState<PodMonitorSnapshot | null>(
    null,
  );
  const [podLoading, setPodLoading] = useState(true);
  const [podError, setPodError] = useState<string | null>(null);

  // ── K8s events state ─────────────────────────────────────────────────────
  const [k8sEvents, setK8sEvents] = useState<K8sEventSnapshot[]>([]);
  const [k8sEventsLoading, setK8sEventsLoading] = useState(true);
  const [k8sEventsError, setK8sEventsError] = useState<string | null>(null);

  // ── GitOps status ────────────────────────────────────────────────────────
  const [gitopsStatus, setGitopsStatus] = useState<GitOpsStatus | null>(null);
  const [gitopsLoading, setGitopsLoading] = useState(false);
  const [gitopsError, setGitopsError] = useState<string | null>(null);

  // ── Metrics ──────────────────────────────────────────────────────────────
  const [metrics, setMetrics] = useState<StackMetrics | null>(null);
  const [metricsLoading, _setMetricsLoading] = useState(true);

  // ── REST fetch functions ─────────────────────────────────────────────────

  const fetchDeploymentEvents = useCallback(async () => {
    if (!alias) return;
    try {
      const url = `${apiBaseUrls.BACKEND}/api/v1/deployment-events/system/${alias}`;
      const res = await axios.get<{ events: DeploymentEvent[]; total: number }>(
        url,
        {
          withCredentials: true,
        },
      );
      setEvents(res.data.events.map(normalizeEvent));
    } catch {
      // Non-fatal
    }
  }, [alias]);

  const fetchPipelineState = useCallback(async (): Promise<PipelineStep[]> => {
    if (!alias) return [];
    try {
      const res = await axios.get<PipelineStep[]>(
        `${apiBaseUrls.BACKEND}/api/v1/applications/${alias}/pipeline-state`,
        { withCredentials: true },
      );
      return res.data;
    } catch {
      return [];
    }
  }, [alias]);

  const fetchPods = useCallback(async () => {
    if (!alias) return;
    setPodLoading(true);
    setPodError(null);
    try {
      const url = `${apiBaseUrls.BACKEND}${ApplicationEndpoints.GET_APPLICATION_PODS.replace(":id", alias)}`;
      const res = await axios.get<PodMonitorSnapshot>(url, {
        withCredentials: true,
      });
      setPodSnapshot(res.data);
    } catch {
      setPodError("Failed to load pod status.");
    } finally {
      setPodLoading(false);
    }
  }, [alias]);

  const fetchK8sEvents = useCallback(async () => {
    if (!alias) return;
    setK8sEventsLoading(true);
    setK8sEventsError(null);
    try {
      const url = `${apiBaseUrls.BACKEND}${ApplicationEndpoints.GET_APPLICATION_EVENTS.replace(":id", alias)}`;
      const res = await axios.get<K8sEventSnapshot[]>(url, {
        withCredentials: true,
      });
      setK8sEvents(res.data);
    } catch {
      setK8sEventsError("Failed to load events.");
    } finally {
      setK8sEventsLoading(false);
    }
  }, [alias]);

  const fetchGitOps = useCallback(async () => {
    if (!alias) return;
    setGitopsLoading(true);
    setGitopsError(null);
    try {
      const res = await axios.get<GitOpsStatus | null>(
        `${apiBaseUrls.BACKEND}/api/v1/applications/${alias}/gitops-status`,
        { withCredentials: true },
      );
      setGitopsStatus(res.data);
    } catch {
      setGitopsError("Failed to load GitOps status");
      setGitopsStatus(null);
    } finally {
      setGitopsLoading(false);
    }
  }, [alias]);

  const fetchMetrics = useCallback(async () => {
    if (!alias) return;
    try {
      const url = `${apiBaseUrls.BACKEND}${ApplicationEndpoints.GET_APPLICATION_METRICS.replace(":id", alias)}`;
      const res = await axios.get<StackMetrics>(url, {
        withCredentials: true,
      });
      setMetrics(res.data);
    } catch {
      // Non-fatal — metrics are best-effort
    }

    // Also reconcile application status from the REST API on every poll.
    // This prevents the header badge from getting stuck at "provisioning" or
    // "failed" when a fast build+deploy completes before the WS subscription
    // receives the STATUS_CHANGED event.
    try {
      const appUrl = `${apiBaseUrls.BACKEND}${ApplicationEndpoints.GET_APPLICATION.replace(":id", alias)}`;
      const appRes = await axios.get<{ status: string }>(appUrl, {
        withCredentials: true,
      });
      if (appRes.data?.status) {
        setStatus((prev) => {
          // Only override if the REST status differs from what we have.
          // Don't regress from a newer WS status if the REST response is stale.
          if (prev !== appRes.data.status) {
            return appRes.data.status;
          }
          return prev;
        });
      }
    } catch {
      // Non-fatal — status reconciliation is best-effort
    }
  }, [alias]);

  // ── Initial data fetch ──────────────────────────────────────────────────

  useEffect(() => {
    if (!alias) return;

    void Promise.all([
      fetchDeploymentEvents(),
      fetchPipelineState().then((state) => setPipelineState(state)),
      fetchPods(),
      fetchK8sEvents(),
      fetchGitOps(),
      fetchMetrics(),
    ]).finally(() => {
      setDeploymentLoading(false);
    });
  }, [
    alias,
    fetchDeploymentEvents,
    fetchPipelineState,
    fetchPods,
    fetchK8sEvents,
    fetchGitOps,
    fetchMetrics,
  ]);

  // ── Re-fetch on org switch ──────────────────────────────────────────────

  useEffect(() => {
    const handler = () => {
      void Promise.all([
        fetchDeploymentEvents(),
        fetchPipelineState().then((state) => setPipelineState(state)),
        fetchPods(),
        fetchK8sEvents(),
        fetchGitOps(),
        fetchMetrics(),
      ]);
    };
    eventBus.subscribeGlobal(OrgSessionEvents.ORG_SESSION_CHANGED, handler);
  }, [
    fetchDeploymentEvents,
    fetchPipelineState,
    fetchPods,
    fetchK8sEvents,
    fetchGitOps,
    fetchMetrics,
  ]);

  return {
    events,
    pipelineState,
    logBatches,
    deploymentLoading,
    deploymentError,
    orgMismatch,
    pipelineRunRefreshSignal,
    status,
    deploymentUrl,
    podSnapshot,
    podLoading,
    podError,
    k8sEvents,
    k8sEventsLoading,
    k8sEventsError,
    gitopsStatus,
    gitopsLoading,
    gitopsError,
    metrics,
    metricsLoading,
    setEvents,
    setPipelineState,
    setLogBatches,
    setDeploymentError,
    setDeploymentLoading,
    setStatus,
    setDeploymentUrl,
    setPodSnapshot,
    setPodLoading,
    setPodError,
    setK8sEvents,
    setK8sEventsLoading,
    setK8sEventsError,
    setGitopsStatus,
    setGitopsLoading,
    setGitopsError,
    setMetrics,
    setPipelineRunRefreshSignal,
    fetchDeploymentEvents,
    fetchPipelineState,
    fetchPods,
    fetchK8sEvents,
    fetchGitOps,
    fetchMetrics,
  };
}
